/**
 * O esqueleto: trilho à esquerda, tela à direita.
 *
 * Só isto vem no primeiro carregamento. Cada tela é um pedaço separado,
 * baixado quando alguém entra nela — foi o motivo de migrar: o sistema
 * anterior era um `app.js` de 5.821 linhas que o navegador precisava
 * baixar e interpretar inteiro para mostrar a primeira indicador.
 */

import { Suspense, lazy, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Trilho } from './componentes/Trilho'
import { Paleta } from './componentes/Paleta'
import { Dica } from './componentes/Dica'
import { Recado } from './componentes/Recado'
import { AvisosAoVivo } from './componentes/AvisosAoVivo'
import { ROTAS } from './rotas'
import { usarSessao } from './ganchos/usarSessao'

/* A tela de entrada é um pedaço à parte, como as outras: quem já está
   logado — o caso normal, todo dia — nunca baixa este código. */
const Entrar = lazy(() => import('./telas/Entrar'))

export function App() {
  const [menuAberto, setMenuAberto] = useState(false)
  const { usuario, carregando } = usarSessao()

  /* ═══ NADA É DESENHADO ANTES DE SABER QUEM É ═══

     Sem esta espera, o painel aparece por um instante e some quando a
     resposta chega — e nesse instante ele já disparou as chamadas das
     telas, que voltam 401 e forçam recarga. Além de feio, é um pedido
     de cada tela a cada carregamento da página.

     A espera é uma requisição só, e a tela em branco dura o que ela
     dura. Nada de "carregando": piscar um aviso por 80 ms chama mais
     atenção para a espera do que a espera merece. */
  if (carregando) return null
  if (!usuario) {
    return (
      <Suspense fallback={null}>
        <Entrar />
      </Suspense>
    )
  }

  return (
    <>
      <Trilho
        aberto={menuAberto}
        aoAlternar={() => setMenuAberto(a => !a)}
        aoNavegar={() => setMenuAberto(false)}
      />
      <main>
        {/* O que aparece enquanto o pedaço da tela está sendo baixado.
            É um cartão vazio com o esqueleto do cabeçalho, e não um
            "Carregando…" centralizado: a página não pula quando o
            conteúdo real chega. */}
        <Suspense fallback={<Carregando />}>
          <Routes>
            <Route path="/" element={<Navigate to="/inteligencia" replace />} />
            {ROTAS.map(r => (
              <Route key={r.caminho} path={r.caminho} element={<r.Tela />} />
            ))}
            {/* Endereço que não existe volta para o começo em vez de
                deixar a área de conteúdo em branco, que é
                indistinguível de um defeito. */}
            <Route path="*" element={<Navigate to="/inteligencia" replace />} />
          </Routes>
        </Suspense>
      </main>

      {/* Os dois são do software inteiro e vivem fora das telas: a
          paleta porque atende de qualquer lugar (Ctrl+K), e a dica
          porque precisa escapar de cartões com `overflow: hidden` —
          dentro deles seria cortada. */}
      <Paleta />
      <Dica />
      {/* Pelo mesmo motivo dos dois acima: o recado nasce dentro de um
          formulário, e dentro dele seria cortado pelo `overflow` do
          cartão. Aqui ele fica por cima de tudo, onde quer que a pessoa
          esteja. */}
      <Recado />
      {/* Fora das telas, como a paleta e a dica: o alerta chega esteja a
          pessoa onde estiver, e trocar de tela não pode perdê-lo. */}
      <AvisosAoVivo />
    </>
  )
}

function Carregando() {
  return (
    <>
      <header className="topo">
        <div>
          <h1 className="esqueleto-linha" style={{ width: 180, height: 26 }} />
          <p className="esqueleto-linha"
             style={{ width: 320, height: 15, marginTop: 6 }} />
        </div>
      </header>
      <div className="pagina ativa">
        <div className="cartao">
          <div className="esqueleto-linha" style={{ height: 180 }} />
        </div>
      </div>
    </>
  )
}
